/**
 * Skeleton.lists — Presets de carga para tablas, listas, feeds y formularios.
 *
 * QUÉ HACE: skeletons de filas de tabla, listas de items, feed de actividad,
 *   tarjeta genérica y modal de formulario, construidos sobre `Skeleton`.
 * QUÉ INVOCA: Skeleton (Skeleton.base).
 * QUÉ EXPORTA: SkeletonTableRows, SkeletonListItems, SkeletonActivityItems,
 *   SkeletonCard, SkeletonForm.
 * QUÉ NO DEBE HACER: no contiene lógica de negocio ni datos reales.
 */

import React from "react";
import { Skeleton } from "./Skeleton.base";

/* ═══════════════════════════════════════════════════════════════
   PRESET: TABLE ROWS (DataTable)
   ═══════════════════════════════════════════════════════════════ */

interface SkeletonTableRowsProps {
  /** Cantidad de filas a mostrar (por defecto 5) */
  rows?: number;
  /** Cantidad de columnas por fila (por defecto 4) */
  cols?: number;
  /** Mostrar checkbox en primera columna (por defecto false) */
  withCheckbox?: boolean;
  className?: string;
}

/**
 * Filas de esqueleto para DataTable. Muestra N filas × M columnas.
 * Se coloca dentro del contenedor de la tabla para mantener el layout.
 */
export const SkeletonTableRows: React.FC<SkeletonTableRowsProps> = ({
  rows = 5,
  cols = 4,
  withCheckbox = false,
  className = "",
}) => (
  <div aria-hidden="true" className={`space-y-0 ${className}`}>
    {Array.from({ length: rows }).map((_, rowIdx) => (
      <div
        key={rowIdx}
        className="flex items-center gap-4 px-4 py-3 border-b border-gray-100 dark:border-slate-700"
      >
        {withCheckbox && (
          <Skeleton
            width="w-4"
            height="h-4"
            rounded="sm"
            className="flex-shrink-0"
          />
        )}
        {/* Primera columna siempre tiene avatar/icono */}
        <Skeleton
          width="w-8"
          height="h-8"
          rounded="full"
          className="flex-shrink-0"
        />
        <div className="flex-1 flex gap-4">
          {Array.from({ length: cols }).map((_, colIdx) => (
            <Skeleton
              key={colIdx}
              height="h-3.5"
              rounded="md"
              className="flex-1"
              /* Variar anchos para que se vea natural */
              width={
                colIdx === 0
                  ? "w-full"
                  : colIdx === cols - 1
                    ? "w-16"
                    : "w-full"
              }
            />
          ))}
        </div>
      </div>
    ))}
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   PRESET: LIST ITEMS (TopProductsList, TeamPerformanceTable)
   ═══════════════════════════════════════════════════════════════ */

interface SkeletonListItemsProps {
  /** Cantidad de items (por defecto 5) */
  items?: number;
  /** Mostrar medalla/badge de ranking a la izquierda (por defecto true) */
  withRank?: boolean;
  className?: string;
}

/**
 * Items de lista — dos variantes:
 * withRank=true  → TopProductsList:      [rank] [nombre | precio] [barra | unidades]
 * withRank=false → TeamPerformanceTable: [avatar] [nombre + ventas] [precio pill]
 */
export const SkeletonListItems: React.FC<SkeletonListItemsProps> = ({
  items = 5,
  withRank = true,
  className = "",
}) => (
  <div aria-hidden="true" className={`space-y-3 ${className}`}>
    {Array.from({ length: items }).map((_, i) =>
      withRank ? (
        /* TopProductsList */
        <div key={i} className="flex items-start gap-2 group">
          {/* Badge de ranking */}
          <Skeleton
            width="w-5"
            height="h-5"
            rounded="full"
            className="flex-shrink-0 mt-0.5"
          />
          <div className="flex-1 space-y-1.5">
            {/* Nombre + precio */}
            <div className="flex items-center justify-between gap-2">
              <Skeleton
                height="h-3.5"
                rounded="md"
                width={i % 3 === 0 ? "w-32" : i % 3 === 1 ? "w-28" : "w-36"}
              />
              <Skeleton
                height="h-3"
                width="w-16"
                rounded="md"
                className="flex-shrink-0"
              />
            </div>
            {/* Barra de progreso + unidades */}
            <div className="flex items-center gap-2">
              <Skeleton
                height="h-1.5"
                width="w-full"
                rounded="full"
                className="flex-1"
              />
              <Skeleton
                height="h-2.5"
                width="w-16"
                rounded="md"
                className="flex-shrink-0"
              />
            </div>
          </div>
        </div>
      ) : (
        /* TeamPerformanceTable */
        <div key={i} className="flex items-center gap-3 px-1.5 py-1 rounded-lg">
          {/* Avatar con iniciales */}
          <Skeleton
            width="w-8"
            height="h-8"
            rounded="full"
            className="flex-shrink-0"
          />
          <div className="flex-1 space-y-1.5">
            <Skeleton
              height="h-3.5"
              rounded="md"
              width={i % 3 === 0 ? "w-28" : i % 3 === 1 ? "w-24" : "w-32"}
            />
            <Skeleton height="h-2.5" rounded="md" width="w-28" />
          </div>
          {/* Precio en pill */}
          <Skeleton
            width="w-16"
            height="h-6"
            rounded="full"
            className="flex-shrink-0"
          />
        </div>
      ),
    )}
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   PRESET: ACTIVITY FEED ITEMS (ActivityFeed)
   ═══════════════════════════════════════════════════════════════ */

interface SkeletonActivityItemsProps {
  /** Cantidad de items (por defecto 4) */
  items?: number;
  className?: string;
}

/**
 * Items del feed de actividad: dot pequeño + acción + entidad•usuario + timestamp.
 * Coincide con ActivityFeed (w-2.5 h-2.5 dot con ring).
 */
export const SkeletonActivityItems: React.FC<SkeletonActivityItemsProps> = ({
  items = 4,
  className = "",
}) => (
  <div aria-hidden="true" className={`space-y-1 ${className}`}>
    {Array.from({ length: items }).map((_, i) => (
      <div key={i} className="flex gap-3 px-2 py-1.5 rounded-lg">
        {/* Dot de severidad — pequeño como el real */}
        <div className="flex-shrink-0 mt-1.5">
          <Skeleton width="w-3" height="h-3" rounded="full" />
        </div>
        {/* Contenido */}
        <div className="flex-1 space-y-1">
          <Skeleton
            height="h-3"
            rounded="md"
            width={i % 3 === 0 ? "w-3/4" : i % 3 === 1 ? "w-2/3" : "w-4/5"}
          />
          <Skeleton
            height="h-2.5"
            rounded="md"
            width={i % 2 === 0 ? "w-2/5" : "w-1/3"}
          />
          <Skeleton height="h-2.5" rounded="md" width="w-1/3" />
        </div>
      </div>
    ))}
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   PRESET: CARD GENÉRICA (contenido de texto)
   ═══════════════════════════════════════════════════════════════ */

interface SkeletonCardProps {
  /** Número de líneas de cuerpo (por defecto 3) */
  lines?: number;
  /** Mostrar imagen/banner en la parte superior (por defecto false) */
  withImage?: boolean;
  /** Alto de la imagen en px (por defecto 120) */
  imageHeight?: number;
  className?: string;
}

/**
 * Tarjeta genérica con título, cuerpo de N líneas y acción.
 * Útil para cualquier tarjeta/panel que cargue contenido dinámico.
 */
export const SkeletonCard: React.FC<SkeletonCardProps> = ({
  lines = 3,
  withImage = false,
  imageHeight = 120,
  className = "",
}) => (
  <div
    aria-hidden="true"
    className={`bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700
      rounded-xl shadow-sm overflow-hidden ${className}`}
  >
    {withImage && (
      <div className="skeleton w-full" style={{ height: imageHeight }} />
    )}
    <div className="p-4 space-y-3">
      {/* Título */}
      <Skeleton width="w-2/3" height="h-5" rounded="md" />
      {/* Cuerpo */}
      <div className="space-y-2">
        {Array.from({ length: lines }).map((_, i) => (
          <Skeleton
            key={i}
            height="h-3"
            rounded="md"
            width={i === lines - 1 ? "w-1/2" : "w-full"}
          />
        ))}
      </div>
      {/* Acción */}
      <div className="flex items-center gap-2 pt-1">
        <Skeleton width="w-20" height="h-8" rounded="lg" />
        <Skeleton width="w-16" height="h-8" rounded="lg" />
      </div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   PRESET: FORM MODAL (crear / editar)
   ═══════════════════════════════════════════════════════════════ */

interface SkeletonFormProps {
  /** Número de campos de formulario (por defecto 4) */
  fields?: number;
  /** Mostrar sección de cabecera con título y descripción (por defecto true) */
  withHeader?: boolean;
  className?: string;
}

/**
 * Formulario de modal: título + N campos label+input + botones de acción.
 * Útil para modales de creación/edición mientras se cargan los datos iniciales.
 */
export const SkeletonForm: React.FC<SkeletonFormProps> = ({
  fields = 4,
  withHeader = true,
  className = "",
}) => (
  <div aria-hidden="true" className={`space-y-5 ${className}`}>
    {/* Header del modal */}
    {withHeader && (
      <div className="space-y-1.5 pb-4 border-b border-gray-100 dark:border-slate-700">
        <Skeleton width="w-48" height="h-6" rounded="md" />
        <Skeleton width="w-72" height="h-3.5" rounded="md" />
      </div>
    )}

    {/* Campos */}
    <div className="space-y-4">
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i} className="space-y-1.5">
          <Skeleton width="w-24" height="h-3" rounded="md" />
          <Skeleton width="w-full" height="h-10" rounded="lg" />
        </div>
      ))}
    </div>

    {/* Botones de acción */}
    <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100 dark:border-slate-700">
      <Skeleton width="w-24" height="h-9" rounded="lg" />
      <Skeleton width="w-28" height="h-9" rounded="lg" />
    </div>
  </div>
);
